<template>
  <!-- One 54-high field row of an AuthGroup (LogIn, Register boards): an
       optional label column, the input, a trailing slot, and the show
       password eye on password rows. `error` tints the label. -->
  <label
    class="auth-row"
    :class="{ 'auth-row--eye': type === 'password', 'is-error': error }"
  >
    <span v-if="label" class="auth-row-label">{{ label }}</span>
    <input
      class="auth-row-input"
      :type="inputType"
      :name="name"
      :value="modelValue"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :readonly="readonly"
      :inputmode="inputmode"
      :aria-label="label ? null : placeholder"
      :aria-invalid="error ? 'true' : null"
      autocapitalize="none"
      autocorrect="off"
      spellcheck="false"
      @input="$emit('update:modelValue', $event.target.value)"
    />
    <slot name="trailing" />
    <button
      v-if="type === 'password'"
      type="button"
      class="auth-row-eye"
      :aria-label="shown ? 'Hide password' : 'Show password'"
      :aria-pressed="shown ? 'true' : 'false'"
      @click="shown = !shown"
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path
          d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
        />
        <circle cx="12" cy="12" r="3" />
        <path v-if="shown" d="M4 4l16 16" />
      </svg>
    </button>
  </label>
</template>

<script>
export default {
  name: "AuthRow",
  props: {
    modelValue: { type: String, default: "" },
    label: { type: String, default: null },
    placeholder: { type: String, default: "" },
    type: { type: String, default: "text" },
    name: { type: String, default: null },
    autocomplete: { type: String, default: "off" },
    inputmode: { type: String, default: null },
    readonly: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  data: () => ({ shown: false }),
  computed: {
    inputType() {
      return this.type === "password" && this.shown ? "text" : this.type;
    },
  },
};
</script>

<style scoped>
.auth-row {
  height: 54px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 14px;
  border-bottom: 1px solid var(--cl-separator);
  cursor: text;
}
.auth-row:last-child {
  border-bottom: none;
}
.auth-row--eye {
  gap: 8px;
  padding-right: 6px;
}
.auth-row:focus-within {
  background: var(--cl-row-stripe);
}
.auth-row-label {
  width: 84px;
  flex-shrink: 0;
  font: var(--cl-body);
  color: var(--cl-label);
  white-space: nowrap;
}
.auth-row.is-error .auth-row-label {
  color: var(--cl-destructive-text);
}
.auth-row-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  padding: 0;
  background: transparent;
  color: var(--cl-label);
  font: var(--cl-body);
  caret-color: var(--cl-accent);
}
.auth-row-input::placeholder {
  color: var(--cl-label-3);
  opacity: 1;
}
.auth-row-input[readonly] {
  color: var(--cl-label-2);
  cursor: default;
}
/* Autofill keeps the cell colour instead of the browser's yellow. */
.auth-row-input:-webkit-autofill {
  -webkit-text-fill-color: var(--cl-label);
  box-shadow: 0 0 0 100px var(--cl-cell) inset;
}
.auth-row-eye {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--cl-r-md);
  background: transparent;
  color: var(--cl-label-3);
  cursor: pointer;
}
.auth-row-eye:hover {
  color: var(--cl-label-2);
}
.auth-row-eye:focus-visible {
  outline: 2px solid var(--cl-accent);
  outline-offset: -4px;
}
</style>
